import { Injectable } from '@angular/core';
import {
  CityParkingCustomerRecentActivityDto,
  CityParkingCustomerSummaryDto,
  CityParkingCustomerTableDto,
  CustomerDashboardStat,
  CustomerRecentActivityItem,
} from '../../models/customer/customer-management.model';
import { CityParkingCustomerInfoResponse } from '../../models/customer.model';

@Injectable({
  providedIn: 'root',
})
export class MapperCustomerManagementService {
  mapTableFromDto(dto: CityParkingCustomerTableDto): {
    items: CityParkingCustomerInfoResponse[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    summary: CityParkingCustomerSummaryDto | null;
    dashboardStats: CustomerDashboardStat[];
    newPercent: number;
    oldPercent: number;
    recentActivity: CustomerRecentActivityItem[];
  } {
    const rawDto = dto as CityParkingCustomerTableDto & {
      Data?: CityParkingCustomerInfoResponse[];
      Total?: number;
      Page?: number;
      PageSize?: number;
      TotalPages?: number;
      Summary?: Record<string, unknown>;
    };

    const summary = this.normalizeSummary(rawDto.summary ?? rawDto.Summary);
    const pageSize = dto.pageSize ?? rawDto.PageSize ?? 20;
    const total = dto.total ?? rawDto.Total ?? 0;
    const rawItems = dto.data ?? rawDto.Data ?? [];
    const items = rawItems.map(item => this.normalizeCustomerItem(item as unknown as Record<string, unknown>));

    return {
      items,
      total,
      page: dto.page ?? rawDto.Page ?? 1,
      pageSize,
      totalPages: dto.totalPages ?? rawDto.TotalPages ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 0),
      summary,
      dashboardStats: summary ? this.mapDashboardStats(summary) : this.emptyDashboardStats(),
      newPercent: summary?.newPercent ?? 0,
      oldPercent: summary?.oldPercent ?? 0,
      recentActivity: summary ? this.mapRecentActivity(summary.recentActivity ?? []) : [],
    };
  }

  mapSearchField(type: string): string {
    return type;
  }

  mapStatusFilter(status: 'all' | 'active' | 'inactive'): string | undefined {
    return status === 'all' ? undefined : status;
  }

  getEmptyDashboardStats(): CustomerDashboardStat[] {
    return this.emptyDashboardStats();
  }

  private normalizeCustomerItem(raw: Record<string, unknown>): CityParkingCustomerInfoResponse {
    const createDateRaw = raw['createDate'] ?? raw['CreateDate'];
    const startDateRaw = raw['startDate'] ?? raw['StartDate'];
    const endDateRaw = raw['endDate'] ?? raw['EndDate'];

    const siteIdRaw = raw['siteId'] ?? raw['SiteId'];
    const clientIdRaw = raw['clientId'] ?? raw['ClientId'];

    return {
      id: Number(raw['id'] ?? raw['Id'] ?? 0),
      name: String(raw['name'] ?? raw['Name'] ?? ''),
      enable: Boolean(raw['enable'] ?? raw['Enable'] ?? false),
      phone: String(raw['phone'] ?? raw['Phone'] ?? ''),
      email: String(raw['email'] ?? raw['Email'] ?? ''),
      remark: String(raw['remark'] ?? raw['Remark'] ?? ''),
      siteId: siteIdRaw != null && siteIdRaw !== '' ? Number(siteIdRaw) : undefined,
      clientId: clientIdRaw != null && clientIdRaw !== '' ? Number(clientIdRaw) : undefined,
      siteName: String(raw['siteName'] ?? raw['SiteName'] ?? ''),
      clientName: String(raw['clientName'] ?? raw['ClientName'] ?? ''),
      createDate: createDateRaw ? new Date(String(createDateRaw)) : undefined,
      startDate: startDateRaw ? new Date(String(startDateRaw)) : undefined,
      endDate: endDateRaw ? new Date(String(endDateRaw)) : undefined,
    };
  }

  private normalizeSummary(raw: unknown): CityParkingCustomerSummaryDto | null {
    if (!raw || typeof raw !== 'object') return null;

    const s = raw as Record<string, unknown>;
    const recentRaw = (s['recentActivity'] ?? s['RecentActivity'] ?? []) as Record<string, unknown>[];

    const recentActivity: CityParkingCustomerRecentActivityDto[] = recentRaw.map(item => ({
      id: Number(item['id'] ?? item['Id'] ?? 0),
      name: String(item['name'] ?? item['Name'] ?? ''),
      info: String(item['info'] ?? item['Info'] ?? '-'),
      createDate: (item['createDate'] ?? item['CreateDate'] ?? null) as string | null,
      enable: Boolean(item['enable'] ?? item['Enable'] ?? false),
    }));

    return {
      totalCustomers: Number(s['totalCustomers'] ?? s['TotalCustomers'] ?? 0),
      activeCount: Number(s['activeCount'] ?? s['ActiveCount'] ?? 0),
      inactiveCount: Number(s['inactiveCount'] ?? s['InactiveCount'] ?? 0),
      newTodayCount: Number(s['newTodayCount'] ?? s['NewTodayCount'] ?? 0),
      newThisWeekCount: Number(s['newThisWeekCount'] ?? s['NewThisWeekCount'] ?? 0),
      newPercent: Number(s['newPercent'] ?? s['NewPercent'] ?? 0),
      oldPercent: Number(s['oldPercent'] ?? s['OldPercent'] ?? 0),
      recentActivity,
    };
  }

  private mapDashboardStats(summary: CityParkingCustomerSummaryDto): CustomerDashboardStat[] {
    return [
      { title: 'ลูกค้าทั้งหมด', value: String(summary.totalCustomers ?? 0), change: '', icon: 'group', color: 'bg-blue-100 text-blue-600' },
      { title: 'ลูกค้าใหม่วันนี้', value: String(summary.newTodayCount ?? 0), change: '', icon: 'person_add', color: 'bg-yellow-100 text-yellow-600' },
      { title: 'ใช้งานอยู่ (Active)', value: String(summary.activeCount ?? 0), change: '', icon: 'how_to_reg', color: 'bg-green-100 text-green-600' },
      { title: 'ระงับการใช้งาน (Inactive)', value: String(summary.inactiveCount ?? 0), change: '', icon: 'person_off', color: 'bg-red-100 text-red-600' },
    ];
  }

  private emptyDashboardStats(): CustomerDashboardStat[] {
    return [
      { title: 'ลูกค้าทั้งหมด', value: '0', change: '', icon: 'group', color: 'bg-blue-100 text-blue-600' },
      { title: 'ลูกค้าใหม่วันนี้', value: '0', change: '', icon: 'person_add', color: 'bg-yellow-100 text-yellow-600' },
      { title: 'ใช้งานอยู่ (Active)', value: '0', change: '', icon: 'how_to_reg', color: 'bg-green-100 text-green-600' },
      { title: 'ระงับการใช้งาน (Inactive)', value: '0', change: '', icon: 'person_off', color: 'bg-red-100 text-red-600' },
    ];
  }

  private mapRecentActivity(items: CityParkingCustomerRecentActivityDto[]): CustomerRecentActivityItem[] {
    return items.map(item => ({
      name: item.name || 'Unknown',
      info: item.info || '-',
      action: 'ลงทะเบียนใหม่',
      timeAgo: this.getTimeAgo(item.createDate),
      initial: (item.name || 'U').charAt(0).toUpperCase(),
      color: this.getRandomColor(item.id || 0),
      status: item.enable,
    }));
  }

  private getTimeAgo(dateInput?: string | null): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชม.ที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เมื่อสักครู่';
  }

  private getRandomColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }
}
